import { useMemo, useRef } from 'react';
import { extractInstanceIds, hasInstanceMarker, replaceInstanceMarkers } from '../../portal/instanceMarker';
import { collectInstanceIds, resolveInstanceMarkers } from '../../portal/resolveInstanceMarkers';
import { useInstanceLabels } from './useInstanceLabel';

/** Shared, so a marker-free string keeps a stable `ids` reference. */
const EMPTY_IDS: string[] = [];

/**
 * `labelOf`, made injective: chart series are keyed by label, so two ids sharing a portal
 * name would collapse into one legend entry. The id is appended only where they collide.
 */
const disambiguate = (ids: string[], labelOf: (id: string) => string): ((id: string) => string) => {
  const labels = new Map<string, string>();
  const occurrences = new Map<string, number>();
  for (const id of ids) {
    const label = labelOf(id);
    labels.set(id, label);
    occurrences.set(label, (occurrences.get(label) ?? 0) + 1);
  }
  return (id) => {
    const label = labels.get(id) ?? labelOf(id);
    return (occurrences.get(label) ?? 0) > 1 ? `${label} (${id})` : label;
  };
};

/**
 * A stable `id -> label` resolver. `useInstanceLabels` hands back a fresh `labelOf` every
 * render, so the cache is keyed on the resolved labels rather than on its identity.
 */
const useInstanceLabelResolver = (ids: string[]): ((id: string) => string) => {
  const { labelOf } = useInstanceLabels(ids);

  const key = ids.map((id) => `${id} ${labelOf(id)}`).join('|');
  const cached = useRef<{ key: string; resolve: (id: string) => string } | null>(null);
  if (cached.current?.key !== key) {
    cached.current = { key, resolve: disambiguate(ids, labelOf) };
  }
  return cached.current.resolve;
};

/** Any value, with every marker replaced by the deployment's name. */
export const useInstanceMarkers = <T>(value: T): T => {
  const ids = useMemo(() => collectInstanceIds(value), [value]);
  const resolve = useInstanceLabelResolver(ids);
  return useMemo(() => resolveInstanceMarkers(value, resolve), [value, resolve]);
};

/** One string, resolved. For what cannot take a node: a tooltip, an `aria-label`, a title. */
export const useInstanceText = (text: string): string => {
  const ids = useMemo(() => (hasInstanceMarker(text) ? extractInstanceIds(text) : EMPTY_IDS), [text]);
  const resolve = useInstanceLabelResolver(ids);
  return useMemo(() => (ids.length === 0 ? text : replaceInstanceMarkers(text, resolve)), [text, ids, resolve]);
};
